import { formatarDia as dia } from '@common/i18n/formatosData';
import {
  Alert,
  Badge,
  Button,
  cn,
  type DateRange,
  EmptyState,
  Input,
  PageHeader,
  ProgressBar,
  Select,
  SelectItem,
  Skeleton,
  Spinner,
  Table,
} from '@ntconsult/aluma-react';
import { Plus } from 'lucide-react';
import { parseAsString, parseAsStringLiteral, useQueryStates } from 'nuqs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useCampanhas, useProgressoCampanhas } from '../hooks';
import type { Campanha } from '../types/Campanha';
import type { Fase, ProgressoDaLista } from '../types/Progresso';
import { CampanhaFormModal } from './CampanhaFormModal';
import { EstadoBadge } from './EstadoBadge';
import { FaseBadge } from './FaseBadge';
import { porcentagem } from './funil';

const FASES = [
  'preparacao',
  'vencida',
  'agendada',
  'aberta',
  'encerrada',
  'pausada',
  'fechada',
] as const satisfies readonly Fase[];
const TODOS = 'todos';

export function CampanhasPage() {
  const { t } = useTranslation(['campanhas', 'common']);
  const navigate = useNavigate();
  const { data, isLoading, isError } = useCampanhas();
  const progresso = useProgressoCampanhas();
  const [criando, setCriando] = useState(false);
  // Poucas campanhas: o filtro é no cliente (spec 22 E); fica na URL para sobreviver ao recarregamento.
  // O filtro é de fase (spec 29 R16), mas o parâmetro continua `estado`; `inativa` vira sem filtro.
  const [f, setF] = useQueryStates({
    q: parseAsString.withDefault(''),
    estado: parseAsStringLiteral(FASES),
    de: parseAsString.withDefault(''),
    ate: parseAsString.withDefault(''),
  });
  const filtrando = !!(f.q || f.estado || f.de || f.ate);

  const porId = new Map(progresso.data?.map((p) => [p.campanhaId, p]));
  // Sem fase não há como filtrar por ela: com o filtro, a lista espera o progresso (R18).
  const esperandoFase = !!f.estado && progresso.isPending;
  const faseFalhou = !!f.estado && progresso.isError;
  const lista = data?.filter(
    (c) =>
      c.nome.toLowerCase().includes(f.q.trim().toLowerCase()) &&
      (!f.estado || porId.get(c.id)?.fase === f.estado) &&
      // Período: a campanha cruza o intervalo escolhido (datas `YYYY-MM-DD` comparam como texto).
      (!f.de || c.fim >= f.de) &&
      (!f.ate || c.inicio <= f.ate),
  );
  const iso = (d: Date | null) =>
    d
      ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      : null;
  const parse = (s: string) => (s ? new Date(`${s}T00:00:00`) : null);

  // A ativa é a primeira linha da tabela, destacada (R14, revista em 2026-10-26: num cartão à
  // parte, acima da tabela, não se via que ela era uma das campanhas da lista).
  const linhas = lista && [...lista.filter((c) => c.ativa), ...lista.filter((c) => !c.ativa)];

  /** Fase, ou o estado de antes se o progresso falhou (R18). */
  const selo = (c: Campanha, p: ProgressoDaLista | undefined) =>
    progresso.isPending ? (
      <Skeleton className="h-5 w-24" />
    ) : p ? (
      <FaseBadge fase={p.fase} />
    ) : (
      <EstadoBadge estado={c.estadoEfetivo} />
    );

  const nova = (
    <Button leftIcon={<Plus />} onClick={() => setCriando(true)}>
      {t('campanhas:nova')}
    </Button>
  );

  return (
    <div className="flex flex-col gap-8">
      <PageHeader title={t('campanhas:titulo')} actions={nova} />

      <section className="flex flex-col gap-4">
        {isLoading && <Spinner label={t('common:loading')} />}
        {isError && <Alert variant="danger" title={t('common:errors.network')} />}

        {data?.length === 0 && <EmptyState title={t('campanhas:vazio')} action={nova} />}

        {data && data.length > 0 && (
          <>
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Input
                type="search"
                className="w-full sm:w-72"
                value={f.q}
                aria-label={t('campanhas:filtros.busca')}
                placeholder={t('campanhas:filtros.busca')}
                onChange={(e) => setF({ q: e.target.value || null })}
              />
              <Select
                value={f.estado ?? TODOS}
                aria-label={t('campanhas:colunas.fase')}
                onValueChange={(v) =>
                  setF({ estado: v === TODOS ? null : (v as (typeof FASES)[number]) })
                }
              >
                <SelectItem value={TODOS}>{t('campanhas:filtros.todasFases')}</SelectItem>
                {FASES.map((e) => (
                  <SelectItem key={e} value={e}>
                    {t(`campanhas:fases.${e}`)}
                  </SelectItem>
                ))}
              </Select>
              {filtrando && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setF({ q: null, estado: null, de: null, ate: null })}
                >
                  {t('campanhas:filtros.limpar')}
                </Button>
              )}
            </div>

            {esperandoFase && <Skeleton className="h-40 w-full" />}
            {faseFalhou && <Alert variant="danger" title={t('common:errors.network')} />}
            {!esperandoFase && !faseFalhou && lista?.length === 0 && (
              <EmptyState title={t('campanhas:semResultado')} />
            )}
            {!esperandoFase && !faseFalhou && linhas && linhas.length > 0 && (
              <Table zebra>
                <Table.Head>
                  <Table.Row>
                    <Table.Header>{t('campanhas:colunas.nome')}</Table.Header>
                    <Table.Header
                      filter={
                        <Table.DateRangeFilter
                          label={t('campanhas:filtros.periodo')}
                          value={{ from: parse(f.de), to: parse(f.ate) }}
                          onValueChange={(r: DateRange) =>
                            setF({ de: iso(r.from), ate: iso(r.to) })
                          }
                        />
                      }
                    >
                      {t('campanhas:colunas.periodo')}
                    </Table.Header>
                    <Table.Header>{t('campanhas:colunas.fase')}</Table.Header>
                    <Table.Header>{t('campanhas:colunas.progresso')}</Table.Header>
                    <Table.Header>{t('campanhas:colunas.acoes')}</Table.Header>
                  </Table.Row>
                </Table.Head>
                <Table.Body>
                  {linhas.map((c) => (
                    // Linha clicável; o foco por teclado fica no link e no botão (sem navegação dupla).
                    <Table.Row
                      key={c.id}
                      className={cn('cursor-pointer', c.ativa && 'bg-brand-primary/5')}
                      onClick={() => navigate(`/campanhas/${c.id}`)}
                      data-campanha-ativa={c.ativa || undefined}
                    >
                      <Table.Cell>
                        <span className="flex flex-wrap items-center gap-2">
                          <Link
                            to={`/campanhas/${c.id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="font-medium underline underline-offset-2"
                          >
                            {c.nome}
                          </Link>
                          {c.ativa && <Badge tone="brand">{t('campanhas:ativaSelo')}</Badge>}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        {dia(c.inicio)} – {dia(c.fim)}
                      </Table.Cell>
                      <Table.Cell>{selo(c, porId.get(c.id))}</Table.Cell>
                      <Table.Cell>
                        <Progresso p={porId.get(c.id)} carregando={progresso.isPending} />
                      </Table.Cell>
                      <Table.Cell>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/campanhas/${c.id}`);
                          }}
                        >
                          {t('campanhas:acoes.ver')}
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            )}
          </>
        )}
      </section>

      <CampanhaFormModal open={criando} onClose={() => setCriando(false)} />
    </div>
  );
}

/** "{{responderam}}/{{naCampanha}}" com a barra; sem ninguém ou sem progresso, "—" (R15, R18). */
function Progresso({ p, carregando }: { p?: ProgressoDaLista; carregando: boolean }) {
  if (carregando) return <Skeleton className="h-5 w-32" />;
  if (!p || p.naCampanha === 0) return <span>—</span>;
  const rotulo = `${p.responderam}/${p.naCampanha}`;
  return (
    <div className="flex min-w-32 items-center gap-2">
      <ProgressBar
        className="flex-1"
        value={porcentagem(p.responderam, p.naCampanha)}
        label={rotulo}
      />
      <span className="text-sm tabular-nums">{rotulo}</span>
    </div>
  );
}
